import { renderRichText } from '../../services/rich-text';
import QRCode from 'qrcode';
import type {
  DocumentDto,
  RentalAgreementDto,
  TenantDto,
  TenantSatisfactionSurveyDto,
  TenantWelcomePageContactDto,
  TenantWelcomePageDto,
} from '../../services/api';
import { documentsApi } from '../../services/api';
import { satisfactionSurveysApi } from '../../services/satisfaction-surveys.api';
import { tenantWelcomePagesApi } from '../../services/tenant-welcome-pages.api';
import { i18nService, t as translate } from '../../services/i18n';
import { icon } from '../../icons';
import {
  escapeHtml,
  formatAgreementPeriod,
  formatDate,
  formatMonthLabel,
  getLatestSurveyEligibleYear,
  getDocumentTypeLabel,
  getRentPaymentStatusClass,
  getRentPaymentStatusLabel,
  hasNewerPaymentHistory,
  hasOlderPaymentHistory,
  isAgreementActive,
  TENANT_ICONS,
} from './tenant-shared';
import type { AccordionTab, PaymentHistoryWindow } from './tenant-types';
import '../timeline/rc-timeline-events';

export class RcTenantAccordion extends HTMLElement {
  private _tenant: TenantDto | null = null;

  get tenant(): TenantDto | null {
    return this._tenant;
  }

  set tenant(value: TenantDto | null) {
    if (this._tenant?.id !== value?.id) {
      this.surveysLoaded = false;
      this.surveys = [];
      this.surveySendError = '';
      this.documentsLoaded = false;
      this.tenantDocuments = [];
      this.welcomePageLoaded = false;
      this.welcomePage = null;
      this.welcomeDraft = null;
      this.welcomeSaveError = '';
      this.copyFeedback = '';
      this.activeTab = 'info';
    }

    this._tenant = value;
  }

  agreements: RentalAgreementDto[] = [];
  loading = false;
  error = false;
  activeTab: AccordionTab = 'info';
  paymentHistoryCache = new Map<string, PaymentHistoryWindow>();
  paymentHistoryErrors = new Set<string>();
  highlightSurveyId: string | null = null;
  surveys: TenantSatisfactionSurveyDto[] = [];
  surveysLoaded = false;
  surveySending = false;
  surveySendError = '';
  tenantDocuments: DocumentDto[] = [];
  documentsLoaded = false;
  welcomePage: TenantWelcomePageDto | null = null;
  welcomeDraft: TenantWelcomePageDto | null = null;
  welcomePageLoaded = false;
  welcomeSaving = false;
  welcomeSaveError = '';
  copyFeedback = '';
  welcomeQrCodeDataUrl = '';
  welcomeQrCodeSourceUrl = '';
  welcomeQrCodeError = '';

  private setActiveTab(tab: AccordionTab): void {
    this.activeTab = tab;
    this.dispatchEvent(new CustomEvent('tenant-accordion-tab-change', {
      bubbles: true,
      detail: {
        tenantId: this.tenant?.id,
        tab,
      },
    }));
  }

  connectedCallback(): void {
    this.addEventListener('click', this.handleClick);
    this.addEventListener('input', this.handleWelcomeInput as EventListener);
    this.render();
  }

  disconnectedCallback(): void {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('input', this.handleWelcomeInput as EventListener);
  }

  async loadSurveys(): Promise<void> {
    if (!this.tenant || this.surveysLoaded) return;

    try {
      this.surveys = await satisfactionSurveysApi.getByTenant(this.tenant.id);
    } catch {
      this.surveys = [];
    } finally {
      this.surveysLoaded = true;
    }

    this.render();
  }

  async loadDocuments(): Promise<void> {
    if (!this.tenant || this.documentsLoaded) return;

    try {
      this.tenantDocuments = await documentsApi.getByTenant(this.tenant.id);
    } catch {
      this.tenantDocuments = [];
    } finally {
      this.documentsLoaded = true;
    }

    this.render();
  }

  async loadWelcomePage(): Promise<void> {
    if (!this.tenant || this.welcomePageLoaded) return;

    try {
      const welcomePage = await tenantWelcomePagesApi.getByTenant(this.tenant.id);
      this.welcomePage = welcomePage;
      this.welcomeDraft = this.cloneWelcomePage(welcomePage);
    } catch {
      this.welcomePage = null;
      this.welcomeDraft = null;
    } finally {
      this.welcomePageLoaded = true;
    }

    this.render();
  }

  scrollToSurveySection(targetSurveyId?: string): void {
    this.setActiveTab('surveys');
    this.highlightSurveyId = targetSurveyId ?? null;
    this.render();
  }

  scrollToWelcomePageSection(): void {
    this.setActiveTab('welcome');
    this.render();
  }

  render(): void {
    if (!this.tenant) {
      this.innerHTML = '';
      return;
    }

    if (this.loading) {
      this.innerHTML = `<div class="accordion-feedback">${translate('common.loading')}</div>`;
      return;
    }

    if (this.error) {
      this.innerHTML = `<div class="empty-state accordion-empty-state">${translate('tenantPage.loadError')}</div>`;
      return;
    }

    const tabs: { id: AccordionTab; label: string }[] = [
      { id: 'info', label: translate('tenantPage.tabs.info') },
      { id: 'payments', label: translate('tenantPage.tabs.payments') },
      { id: 'timeline', label: translate('tenantPage.tabs.timeline') },
      { id: 'documents', label: translate('tenantPage.tabs.documents') },
      { id: 'placeDescriptions', label: translate('tenantPage.tabs.placeDescriptions') },
      { id: 'dossier', label: translate('tenantPage.tabs.dossier') },
      { id: 'welcome', label: translate('tenantPage.tabs.welcomePage') },
      { id: 'surveys', label: translate('tenantPage.tabs.surveys') },
    ];

    const tabBar = `
      <div class="accordion-tabs" role="tablist">
        ${tabs.map((tab) => `
          <button
            class="accordion-tab${this.activeTab === tab.id ? ' accordion-tab--active' : ''}"
            data-action="tab"
            data-tab="${tab.id}"
            role="tab"
            aria-selected="${this.activeTab === tab.id}">
            ${tab.label}
          </button>
        `).join('')}
      </div>`;

    let panelContent = '';
    switch (this.activeTab) {
      case 'info':
        panelContent = this.renderInfoPanel();
        break;
      case 'payments':
        panelContent = this.renderPaymentsPanel();
        break;
      case 'timeline':
        panelContent = this.renderTimelinePanel();
        break;
      case 'documents':
        panelContent = this.renderDocumentsPanel();
        break;
      case 'placeDescriptions':
        panelContent = this.renderPlaceDescriptionsPanel();
        break;
      case 'dossier':
        panelContent = this.renderDossierPanel();
        break;
      case 'welcome':
        panelContent = this.renderWelcomePageSection();
        break;
      case 'surveys':
        panelContent = this.renderSurveySection();
        break;
    }

    this.innerHTML = `
      ${tabBar}
      <div class="accordion-tab-panel" role="tabpanel">
        ${panelContent}
      </div>`;
  }

  private renderInfoPanel(): string {
    const tenant = this.tenant!;
    const activeAgreements = this.agreements.filter((agreement) => isAgreementActive(agreement));
    const latestAgreement = this.agreements[0] ?? null;
    let html = '';

    html += `<div class="accordion-info-shell">`;
    html += `<div class="accordion-summary-grid">`;
    html += `<div class="accordion-summary-card"><span class="accordion-summary-label">${translate('tenantPage.agreementsTitle')}</span><strong class="accordion-summary-value">${this.agreements.length}</strong></div>`;
    html += `<div class="accordion-summary-card"><span class="accordion-summary-label">${translate('common.active')}</span><strong class="accordion-summary-value">${activeAgreements.length}</strong></div>`;
    html += `<div class="accordion-summary-card"><span class="accordion-summary-label">${translate('common.property')}</span><strong class="accordion-summary-value">${latestAgreement ? escapeHtml(latestAgreement.propertyAddress) : '&mdash;'}</strong></div>`;
    html += `</div>`;

    html += `<div class="accordion-section-block">`;
    html += `<p class="accordion-section-title">${translate('tenantPage.personalInfo')}</p>`;
    html += `<div class="accordion-info-grid">`;
    if (tenant.email) html += `<span class="accordion-info-label">${translate('common.email')}</span><span>${escapeHtml(tenant.email)}</span>`;
    if (tenant.phone) html += `<span class="accordion-info-label">${translate('common.phone')}</span><span>${escapeHtml(tenant.phone)}</span>`;
    if (!tenant.email && !tenant.phone) html += `<span class="accordion-info-label accordion-info-empty">${translate('tenantPage.noContact')}</span>`;
    html += `</div>`;
    html += `</div>`;

    html += `<div class="accordion-section-block">`;
    html += `<div class="accordion-section-header"><p class="accordion-section-title">${translate('tenantPage.portalSectionTitle')}</p><span class="accordion-section-meta">${translate('tenantPage.tabs.info')}</span></div>`;
    html += `<p class="tenant-action-panel-copy">${translate('tenantPage.portalSectionDescription')}</p>`;
    html += `<div class="card-actions">`;
    if (tenant.tenantPortalConnectionStatus === 'has-account-unlinked') {
      html += `<button class="btn btn-primary btn-sm" data-action="link-portal-account" data-id="${tenant.id}">${TENANT_ICONS.link}${translate('tenantPage.portalLinkAction')}</button>`;
    }
    if ((tenant.tenantPortalConnectionStatus === 'no-account-unlinked'
      || tenant.tenantPortalConnectionStatus === 'no-account-invite-sent') && tenant.email) {
      html += `<button class="btn btn-outline btn-sm" data-action="invite-portal-account" data-id="${tenant.id}">${TENANT_ICONS.home}${translate(tenant.tenantPortalConnectionStatus === 'no-account-invite-sent' ? 'tenantPage.portalInviteResendAction' : 'tenantPage.portalInviteAction')}</button>`;
    }
    if (tenant.tenantPortalConnectionStatus !== 'has-account-unlinked'
      && !((tenant.tenantPortalConnectionStatus === 'no-account-unlinked'
        || tenant.tenantPortalConnectionStatus === 'no-account-invite-sent') && tenant.email)) {
      html += `<span class="badge badge-muted">${translate('tenantPage.portalActionsUnavailable')}</span>`;
    }
    html += `</div>`;
    html += `</div>`;

    if (tenant.notes) {
      html += `<div class="accordion-section-block">`;
      html += `<p class="accordion-section-title">${translate('common.notes')}</p>`;
      html += `<div class="accordion-notes">${renderRichText(tenant.notes)}</div>`;
      html += `</div>`;
    }

    html += `<div class="accordion-section-block">`;
    html += `<div class="accordion-section-header"><p class="accordion-section-title">${translate('tenantPage.agreementsTitle')}</p><span class="accordion-section-meta">${translate('tenantPage.tabs.info')}</span></div>`;
    if (this.agreements.length === 0) {
      html += `<div class="empty-state accordion-empty-inline"><p>${translate('tenantPage.noAgreements')}</p></div>`;
    } else {
      html += `<div class="accordion-table-wrap">
        <table class="accordion-agreements-table">
          <thead>
            <tr>
              <th>${translate('common.property')}</th>
              <th>${translate('property.rent')}</th>
              <th>${translate('common.startDate')}</th>
              <th>${translate('common.endDate')}</th>
              <th>${translate('common.status')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            ${this.agreements.map((agreement) => `
              <tr>
                <td>${escapeHtml(agreement.propertyAddress)}</td>
                <td>${agreement.rentAmount != null ? `${escapeHtml(i18nService.formatCurrency(agreement.rentAmount))}${escapeHtml(translate('property.perMonth'))}` : '-'}</td>
                <td>${formatDate(agreement.startDate)}</td>
                <td>${agreement.endDate ? formatDate(agreement.endDate) : '-'}</td>
                <td><span class="badge ${isAgreementActive(agreement) ? 'badge-success' : 'badge-muted'}">${isAgreementActive(agreement) ? translate('common.active') : translate('common.ended')}</span></td>
                <td>
                  <div class="card-actions">
                    <button class="btn-icon" data-action="edit-agreement" data-id="${agreement.id}" data-agreement-id="${agreement.id}" title="${translate('common.edit')}">
                      ${TENANT_ICONS.edit}
                    </button>
                    ${isAgreementActive(agreement) ? `<button class="btn-icon btn-icon-primary" data-action="rent-change" data-id="${agreement.id}" data-agreement-id="${agreement.id}" title="${translate('tenantPage.changeRent')}">
                      ${TENANT_ICONS.trend}
                    </button>` : ''}
                  </div>
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>`;
    }
    html += `</div>`;
    html += `</div>`;

    return html;
  }

  private renderPaymentsPanel(): string {
    const agreementsWithHistory = this.agreements.filter((agreement) => !!agreement.startDate);
    if (agreementsWithHistory.length === 0) {
      return `<div class="empty-state accordion-empty-inline"><p>${translate('tenantPayments.empty')}</p></div>`;
    }
    return agreementsWithHistory.map((agreement) => this.renderPaymentHistoryCard(agreement)).join('');
  }

  private renderTimelinePanel(): string {
    const tenant = this.tenant!;
    return `
      <div class="accordion-section-block">
        <div class="accordion-section-header">
          <p class="accordion-section-title">${translate('tenantPage.timeline')}</p>
          <span class="accordion-section-meta">${escapeHtml(`${tenant.firstName} ${tenant.lastName}`)}</span>
        </div>
        <rc-timeline-events tenant-id="${tenant.id}" tenant-name="${escapeHtml(`${tenant.firstName} ${tenant.lastName}`)}"></rc-timeline-events>
      </div>`;
  }

  private renderDocumentsPanel(): string {
    if (!this.documentsLoaded) {
      if (this.tenant) void this.loadDocuments();
      return `<p class="accordion-feedback">${translate('common.loading')}</p>`;
    }

    return `
      <div class="accordion-section-block">
        <div class="accordion-section-header">
          <p class="accordion-section-title">${translate('common.documents')}</p>
          <span class="accordion-section-meta">${this.tenantDocuments.length}</span>
        </div>
        <p class="tenant-action-panel-copy">${translate('tenantPage.documentsTabDescription')}</p>
        <div class="card-actions">
          <button class="btn btn-primary btn-sm" data-action="open-documents">${TENANT_ICONS.plusSm}${translate('document.upload')}</button>
        </div>
        ${this.tenantDocuments.length === 0
          ? `<div class="empty-state accordion-empty-inline"><p>${translate('tenant.documents.empty')}</p></div>`
          : `<div class="accordion-table-wrap">
              <table class="accordion-agreements-table">
                <thead>
                  <tr>
                    <th>${translate('common.file')}</th>
                    <th>${translate('common.type')}</th>
                    <th>${translate('common.expiryDate')}</th>
                    <th>${translate('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  ${this.tenantDocuments.map((document) => `
                    <tr>
                      <td>${escapeHtml(document.fileName)}</td>
                      <td><span class="badge badge-info">${escapeHtml(getDocumentTypeLabel(document.documentType))}</span></td>
                      <td>${document.expiryDate ? formatDate(document.expiryDate) : '-'}</td>
                      <td>
                        <div class="card-actions">
                          <button class="btn btn-outline btn-sm" data-action="download-document" data-document-id="${document.id}">${translate('common.download')}</button>
                          <button class="btn btn-outline btn-sm" data-action="open-documents">${translate('common.open')}</button>
                        </div>
                      </td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>`}
      </div>`;
  }

  private renderPlaceDescriptionsPanel(): string {
    return this.renderActionPanel({
      title: translate('placeDescription.buttonShort'),
      description: translate('tenantPage.placeDescriptionsTabDescription'),
      buttonLabel: translate('tenantPage.placeDescriptionsTabAction'),
      action: 'open-place-descriptions',
    });
  }

  private renderDossierPanel(): string {
    return this.renderActionPanel({
      title: translate('dossier.buttonShort'),
      description: translate('tenantPage.dossierTabDescription'),
      buttonLabel: translate('tenantPage.dossierTabAction'),
      action: 'export-dossier',
    });
  }

  private renderActionPanel(options: {
    title: string;
    description: string;
    buttonLabel: string;
    action: string;
  }): string {
    return `
      <div class="accordion-section-block">
        <div class="accordion-section-header">
          <p class="accordion-section-title">${options.title}</p>
          <span class="accordion-section-meta">${translate('tenantPage.tabs.actions')}</span>
        </div>
        <p class="tenant-action-panel-copy">${options.description}</p>
        <div class="card-actions">
          <button class="btn btn-primary btn-sm" data-action="${options.action}">${options.buttonLabel}</button>
        </div>
      </div>`;
  }

  private renderWelcomePageSection(): string {
    if (!this.welcomePageLoaded) {
      if (this.tenant) void this.loadWelcomePage();
      return `<p class="accordion-feedback">${translate('common.loading')}</p>`;
    }

    const draft = this.welcomeDraft ?? this.getEmptyWelcomeDraft();
    const contacts = draft.emergencyContacts.length > 0
      ? draft.emergencyContacts
      : [{ label: '', name: '', phone: '' }];

    const currentStatus = !draft.isConfigured
      ? translate('tenantWelcome.status.notConfigured')
      : !draft.isTenantCurrent
      ? translate('tenantWelcome.status.ended')
      : draft.isActive
      ? translate('tenantWelcome.status.active')
      : translate('tenantWelcome.status.inactive');

    const activePublicUrl = draft.isActive ? draft.publicUrl ?? '' : '';
    const hasActivePublicPage = activePublicUrl.length > 0;

    const publicLink = hasActivePublicPage
      ? `<div class="welcome-link-box">
          <span class="welcome-link-label">${translate('tenantWelcome.publicLink')}</span>
          <code>${escapeHtml(activePublicUrl)}</code>
        </div>`
      : !draft.isActive
        ? ''
        : `<p class="welcome-link-placeholder">${translate('tenantWelcome.linkUnavailable')}</p>`;

    const qrPanel = hasActivePublicPage
      ? this.renderWelcomeQrPanel(activePublicUrl)
      : '';

    const activateLabel = this.welcomeSaving
      ? translate('common.saving')
      : translate('tenantWelcome.activateButton');

    const inactiveWarning = draft.isConfigured && !draft.isActive
      ? `<div class="alert alert-warning survey-helper-alert" role="alert">
          ${icon('alert-circle', { size: 16, attrs: 'style="flex-shrink:0;margin-top:1px"' })}
          <span>${translate('tenantWelcome.inactiveWarning')}</span>
        </div>`
      : '';

    const endWarning = !draft.isTenantCurrent
      ? `<div class="alert alert-warning survey-helper-alert" role="alert">
          ${icon('alert-circle', { size: 16, attrs: 'style="flex-shrink:0;margin-top:1px"' })}
          <span>${translate('tenantWelcome.endedTenantWarning')}</span>
        </div>`
      : '';

    const saveError = this.welcomeSaveError
      ? `<p class="survey-feedback-error">${escapeHtml(this.welcomeSaveError)}</p>`
      : '';

    const copyFeedback = this.copyFeedback
      ? `<p class="welcome-copy-feedback">${escapeHtml(this.copyFeedback)}</p>`
      : '';

    return `
      <div class="welcome-toolbar">
        <span class="badge ${draft.isConfigured && draft.isActive && draft.isTenantCurrent ? 'badge-success' : 'badge-muted'}">${escapeHtml(currentStatus)}</span>
        ${draft.updatedAt ? `<span class="welcome-updated">${translate('tenantWelcome.updatedAt')}: ${escapeHtml(i18nService.formatDate(draft.updatedAt))}</span>` : ''}
      </div>
      ${inactiveWarning}
      <p class="welcome-help">${translate('tenantWelcome.help')}</p>
      ${publicLink}
      ${qrPanel}
      ${hasActivePublicPage
        ? `<div class="welcome-link-actions">
            <button class="btn btn-outline btn-sm" data-action="copy-welcome-link">
              ${translate('tenantWelcome.copyLink')}
            </button>
            <button class="btn btn-outline btn-sm" data-action="print-welcome-qr">
              ${translate('tenantWelcome.printQr')}
            </button>
            <a class="btn btn-outline btn-sm" href="${activePublicUrl}" target="_blank" rel="noreferrer">${translate('tenantWelcome.openPublicPage')}</a>
          </div>`
        : ''}
      ${copyFeedback}
      ${endWarning}
      <div class="welcome-form-grid">
        <div class="form-group">
          <label>${translate('tenantWelcome.pageTitle')}</label>
          <input type="text" data-welcome-field="pageTitle" value="${escapeHtml(draft.pageTitle || '')}" placeholder="${translate('tenantWelcome.pageTitlePlaceholder')}">
        </div>
        <div class="form-group">
          <label>${translate('tenantWelcome.wifiName')}</label>
          <input type="text" data-welcome-field="wifiName" value="${escapeHtml(draft.wifiName || '')}" placeholder="${translate('tenantWelcome.wifiNamePlaceholder')}">
        </div>
        <div class="form-group">
          <label>${translate('tenantWelcome.wifiPassword')}</label>
          <input type="text" data-welcome-field="wifiPassword" value="${escapeHtml(draft.wifiPassword || '')}" placeholder="${translate('tenantWelcome.wifiPasswordPlaceholder')}">
        </div>
        <div class="form-group">
          <label>${translate('tenantWelcome.introduction')}</label>
          <textarea rows="3" data-welcome-field="introduction" placeholder="${translate('tenantWelcome.introductionPlaceholder')}">${escapeHtml(draft.introduction || '')}</textarea>
        </div>
        <div class="form-group">
          <label>${translate('tenantWelcome.houseRules')}</label>
          <textarea rows="4" data-welcome-field="houseRules" placeholder="${translate('tenantWelcome.houseRulesPlaceholder')}">${escapeHtml(draft.houseRules || '')}</textarea>
        </div>
        <div class="form-group">
          <label>${translate('tenantWelcome.trashCollectionInfo')}</label>
          <textarea rows="3" data-welcome-field="trashCollectionInfo" placeholder="${translate('tenantWelcome.trashCollectionPlaceholder')}">${escapeHtml(draft.trashCollectionInfo || '')}</textarea>
        </div>
        <div class="form-group">
          <label>${translate('tenantWelcome.parkingInfo')}</label>
          <textarea rows="3" data-welcome-field="parkingInfo" placeholder="${translate('tenantWelcome.parkingPlaceholder')}">${escapeHtml(draft.parkingInfo || '')}</textarea>
        </div>
        <div class="form-group">
          <label>${translate('tenantWelcome.additionalInfo')}</label>
          <textarea rows="3" data-welcome-field="additionalInfo" placeholder="${translate('tenantWelcome.additionalInfoPlaceholder')}">${escapeHtml(draft.additionalInfo || '')}</textarea>
        </div>
      </div>
      <div class="welcome-contact-block">
        <div class="welcome-contact-header">
          <h4>${translate('tenantWelcome.contactsTitle')}</h4>
          <button class="btn btn-outline btn-sm" data-action="add-welcome-contact">${translate('tenantWelcome.addContact')}</button>
        </div>
        <div class="welcome-contact-list">
          ${contacts.map((contact, index) => `
            <div class="welcome-contact-row">
              <input class="form-control" type="text" data-welcome-contact-field="label" data-contact-index="${index}" value="${escapeHtml(contact.label || '')}" placeholder="${translate('tenantWelcome.contactLabelPlaceholder')}">
              <input class="form-control" type="text" data-welcome-contact-field="name" data-contact-index="${index}" value="${escapeHtml(contact.name || '')}" placeholder="${translate('tenantWelcome.contactNamePlaceholder')}">
              <input class="form-control" type="text" data-welcome-contact-field="phone" data-contact-index="${index}" value="${escapeHtml(contact.phone || '')}" placeholder="${translate('tenantWelcome.contactPhonePlaceholder')}">
              <button class="btn btn-outline btn-sm" data-action="remove-welcome-contact" data-contact-index="${index}">${translate('common.delete')}</button>
            </div>
          `).join('')}
        </div>
      </div>
      <div class="welcome-actions">
        <button class="btn btn-outline" data-action="save-welcome-page" ${this.welcomeSaving ? 'disabled' : ''}>${this.welcomeSaving ? translate('common.saving') : translate('tenantWelcome.saveDraft')}</button>
        ${!draft.isActive
          ? `<button class="btn btn-primary" data-action="activate-welcome-page" ${this.welcomeSaving || !draft.isTenantCurrent ? 'disabled' : ''}>${activateLabel}</button>`
          : ''}
        ${draft.isActive
          ? `<button class="btn btn-outline" data-action="deactivate-welcome-page" ${this.welcomeSaving || !draft.isConfigured ? 'disabled' : ''}>${translate('tenantWelcome.deactivateButton')}</button>`
          : ''}
      </div>
      ${saveError}`;
  }

  private renderSurveySection(): string {
    if (!this.surveysLoaded) {
      if (this.tenant) void this.loadSurveys();
      return `<p class="accordion-feedback">${translate('common.loading')}</p>`;
    }

    const currentYear = new Date().getFullYear();
    const currentYearSurvey = this.surveys.find((survey) => survey.year === currentYear);
    const tenantHasEmail = !!this.tenant?.email;
    const latestEligibleYear = getLatestSurveyEligibleYear(this.agreements);
    const canSendThisYear = latestEligibleYear === currentYear;
    const surveyButtonLabel = this.surveySending
      ? translate('survey.sending')
      : currentYearSurvey
      ? translate('survey.resendButton')
      : translate('survey.sendButton');
    const surveyButtonIcon = this.surveySending
      ? icon('repeat', { size: 16, className: 'survey-send-button-icon survey-send-button-spinner' })
      : currentYearSurvey
      ? icon('refresh-ccw', { size: 16, className: 'survey-send-button-icon' })
      : icon('mail', { size: 16, className: 'survey-send-button-icon' });

    const sendButton = !canSendThisYear
      ? `<span class="badge badge-muted survey-status-badge">${translate('survey.onlyLatestAgreementYear')}</span>`
      : !tenantHasEmail
      ? `<button class="btn btn-primary btn-sm survey-send-button" data-action="send-survey" disabled style="cursor:not-allowed;">
          ${icon('mail', { size: 16, className: 'survey-send-button-icon' })}
          <span>${translate('survey.sendButton')}</span>
        </button>`
      : currentYearSurvey?.answeredAt
      ? `<span class="badge badge-muted survey-status-badge">${translate('survey.alreadySentThisYear')}</span>`
      : `<button class="btn btn-primary btn-sm survey-send-button${this.surveySending ? ' is-loading' : ''}" data-action="send-survey" ${this.surveySending ? 'disabled' : ''}>
          ${surveyButtonIcon}
          <span>${surveyButtonLabel}</span>
        </button>`;

    const helperHtml = !canSendThisYear
      ? ''
      : !tenantHasEmail
      ? `<div class="alert alert-warning survey-helper-alert" role="alert">
          ${icon('alert-circle', { size: 16, attrs: 'style="flex-shrink:0;margin-top:1px"' })}
          <span>${translate('survey.noEmailHint')}</span>
        </div>`
      : '';

    const errorHtml = this.surveySendError
      ? `<p class="survey-feedback-error">${escapeHtml(this.surveySendError)}</p>`
      : '';

    const historyHtml = this.surveys.length === 0
      ? `<p class="survey-history-empty">${translate('survey.noResults')}</p>`
      : `<table class="survey-history-table" style="font-size:.875rem;">
          <thead><tr>
            <th>${translate('survey.year')}</th>
            <th>${translate('survey.rating')}</th>
            <th>${translate('common.status')}</th>
            <th>${translate('survey.comment')}</th>
          </tr></thead>
          <tbody>
            ${this.surveys.map((survey) => `
              <tr data-survey-row-id="${survey.id}" class="${this.highlightSurveyId === survey.id ? 'survey-result--highlight' : ''}">
                <td>${survey.year}</td>
                <td>${survey.rating != null ? `${survey.rating}/5 ${'★'.repeat(survey.rating)}` : '&mdash;'}</td>
                <td><span class="badge ${survey.answeredAt ? 'badge-success' : 'badge-muted'}">${survey.answeredAt ? translate('survey.answered') : translate('survey.pending')}</span></td>
                <td style="max-width:200px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${survey.comment ? escapeHtml(survey.comment) : '&mdash;'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>`;

    return `${helperHtml ? `<div class="survey-action-row">${sendButton}${helperHtml}</div>` : `<div class="survey-action-row">${sendButton}</div>`}${errorHtml}${historyHtml}`;
  }

  private renderPaymentHistoryCard(agreement: RentalAgreementDto): string {
    if (this.paymentHistoryErrors.has(agreement.id)) {
      return `
        <div class="rent-payment-card">
          <div class="rent-payment-card-header">
            <div>
              <div class="rent-payment-card-title">${escapeHtml(agreement.propertyAddress)}</div>
              <div class="rent-payment-card-meta">${escapeHtml(formatAgreementPeriod(agreement))}</div>
            </div>
          </div>
          <div class="empty-state rent-payment-empty">
            <p>${translate('tenantPayments.unavailable')}</p>
          </div>
        </div>`;
    }

    const historyWindow = this.paymentHistoryCache.get(agreement.id);
    const history = historyWindow?.months ?? [];
    const canGoOlder = hasOlderPaymentHistory(agreement, history);
    const canGoNewer = hasNewerPaymentHistory(agreement, history);

    return `
      <div class="rent-payment-card">
        <div class="rent-payment-card-header">
          <div>
            <div class="rent-payment-card-title">${escapeHtml(agreement.propertyAddress)}</div>
            <div class="rent-payment-card-meta">
              ${escapeHtml(formatAgreementPeriod(agreement))}
              ${agreement.rentAmount != null ? ` · ${escapeHtml(`${i18nService.formatCurrency(agreement.rentAmount)}${translate('property.perMonth')}`)}` : ''}
            </div>
          </div>
          <div class="rent-payment-card-actions">
            <button type="button" class="btn btn-outline btn-sm" data-action="payment-history-prev" data-id="${agreement.id}" data-agreement-id="${agreement.id}" ${canGoOlder ? '' : 'disabled'}>
              ${translate('common.previous')}
            </button>
            <button type="button" class="btn btn-outline btn-sm" data-action="payment-history-next" data-id="${agreement.id}" data-agreement-id="${agreement.id}" ${canGoNewer ? '' : 'disabled'}>
              ${translate('common.next')}
            </button>
          </div>
        </div>
        ${history.length === 0
          ? `<div class="empty-state rent-payment-empty"><p>${translate('tenantPayments.empty')}</p></div>`
          : `<div class="rent-payment-grid">
              ${history.map((entry) => `
                <button
                  type="button"
                  class="rent-payment-cell ${getRentPaymentStatusClass(entry.status)}"
                  data-action="payment-log"
                  data-id="${agreement.id}"
                  data-agreement-id="${agreement.id}"
                  data-year="${entry.year}"
                  data-month="${entry.month}">
                  <span class="rent-payment-cell-month">${escapeHtml(formatMonthLabel(entry.year, entry.month))}</span>
                  <span class="rent-payment-cell-state">${escapeHtml(getRentPaymentStatusLabel(entry.status))}</span>
                </button>
              `).join('')}
            </div>`}
      </div>`;
  }

  private handleClick = (event: Event): void => {
    const trigger = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-action]');
    if (!trigger || !this.tenant) return;

    if (trigger.dataset.action === 'tab' && trigger.dataset.tab) {
      this.setActiveTab(trigger.dataset.tab as AccordionTab);
      this.render();
      return;
    }

    const agreementId = trigger.dataset.agreementId ?? trigger.dataset.id;
    const baseDetail = {
      tenantId: this.tenant.id,
      agreementId,
      year: trigger.dataset.year ? Number(trigger.dataset.year) : undefined,
      month: trigger.dataset.month ? Number(trigger.dataset.month) : undefined,
    };

    switch (trigger.dataset.action) {
      case 'edit-agreement':
        this.dispatchSliceEvent('tenant-edit-agreement', baseDetail);
        break;
      case 'rent-change':
        this.dispatchSliceEvent('tenant-rent-change', baseDetail);
        break;
      case 'payment-log':
        this.dispatchSliceEvent('tenant-payment-log', baseDetail);
        break;
      case 'payment-history-prev':
        this.dispatchSliceEvent('tenant-payment-history-prev', baseDetail);
        break;
      case 'payment-history-next':
        this.dispatchSliceEvent('tenant-payment-history-next', baseDetail);
        break;
      case 'link-portal-account':
        this.dispatchSliceEvent('tenant-link-portal-account', { tenantId: this.tenant.id });
        break;
      case 'invite-portal-account':
        this.dispatchSliceEvent('tenant-invite-portal-account', { tenantId: this.tenant.id });
        break;
      case 'open-documents':
        this.dispatchSliceEvent('tenant-open-documents', { tenantId: this.tenant.id });
        break;
      case 'open-place-descriptions':
        this.dispatchSliceEvent('tenant-open-place-descriptions', { tenantId: this.tenant.id });
        break;
      case 'export-dossier':
        this.dispatchSliceEvent('tenant-export-dossier', { tenantId: this.tenant.id });
        break;
      case 'download-document':
        if (trigger.dataset.documentId) {
          void documentsApi.download(trigger.dataset.documentId);
        }
        break;
      case 'send-survey':
        void this.sendSurvey();
        break;
      case 'save-welcome-page':
        void this.saveWelcomePage(this.welcomeDraft?.isActive ?? this.welcomePage?.isActive ?? false);
        break;
      case 'activate-welcome-page':
        void this.saveWelcomePage(true);
        break;
      case 'deactivate-welcome-page':
        void this.saveWelcomePage(false);
        break;
      case 'copy-welcome-link':
        void this.copyWelcomeLink();
        break;
      case 'print-welcome-qr':
        void this.printWelcomeQrCode();
        break;
      case 'add-welcome-contact':
        this.addWelcomeContact();
        break;
      case 'remove-welcome-contact':
        this.removeWelcomeContact(Number(trigger.dataset.contactIndex));
        break;
      default:
        break;
    }
  };

  private handleWelcomeInput = (event: Event): void => {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement | null;
    if (!target) return;

    if (target.dataset.welcomeField) {
      const draft = this.getCurrentWelcomeDraft();
      const field = target.dataset.welcomeField as keyof TenantWelcomePageDto;
      (draft[field] as string | null | undefined) = target.value;
      this.welcomeDraft = draft;
      return;
    }

    if (target.dataset.welcomeContactField) {
      const draft = this.getCurrentWelcomeDraft();
      const index = Number(target.dataset.contactIndex);
      if (Number.isNaN(index)) return;

      const contacts = [...draft.emergencyContacts];
      const current = contacts[index] ?? { label: '', name: '', phone: '' };
      const field = target.dataset.welcomeContactField as keyof TenantWelcomePageContactDto;
      contacts[index] = {
        ...current,
        [field]: target.value,
      };
      draft.emergencyContacts = contacts;
      this.welcomeDraft = draft;
    }
  };

  private async sendSurvey(): Promise<void> {
    if (!this.tenant || this.surveySending) return;

    this.surveySending = true;
    this.surveySendError = '';
    this.render();

    try {
      const dto = await satisfactionSurveysApi.sendToTenant(this.tenant.id);
      const existingIndex = this.surveys.findIndex((survey) => survey.id === dto.id);
      if (existingIndex >= 0) {
        this.surveys = this.surveys.map((survey, index) => index === existingIndex ? dto : survey);
      } else {
        this.surveys = [dto, ...this.surveys];
      }
    } catch (err: unknown) {
      this.surveySendError = err instanceof Error ? err.message : translate('survey.sendError');
    } finally {
      this.surveySending = false;
    }

    this.render();
  }

  private async copyWelcomeLink(): Promise<void> {
    const url = this.welcomeDraft?.publicUrl;
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
      this.copyFeedback = translate('tenantWelcome.copySuccess');
    } catch {
      this.copyFeedback = translate('tenantWelcome.copyError');
    }

    this.render();
  }

  private renderWelcomeQrPanel(url: string): string {
    if (this.welcomeQrCodeSourceUrl !== url) {
      this.welcomeQrCodeDataUrl = '';
      this.welcomeQrCodeError = '';
      this.welcomeQrCodeSourceUrl = url;
      void this.generateWelcomeQrCode(url);
    }

    return `
      <div class="welcome-qr-box">
        <div class="welcome-qr-copy">
          <span class="welcome-link-label">${translate('tenantWelcome.qrTitle')}</span>
          <p>${translate('tenantWelcome.qrHelp')}</p>
        </div>
        <div class="welcome-qr-preview">
          ${this.welcomeQrCodeDataUrl
            ? `<img class="welcome-qr-image" src="${this.welcomeQrCodeDataUrl}" alt="${escapeHtml(translate('tenantWelcome.qrAlt'))}">`
            : this.welcomeQrCodeError
              ? `<p class="welcome-link-placeholder">${escapeHtml(this.welcomeQrCodeError)}</p>`
              : `<p class="welcome-link-placeholder">${translate('common.loading')}</p>`}
        </div>
      </div>`;
  }

  private async generateWelcomeQrCode(url: string): Promise<void> {
    try {
      const qrCodeDataUrl = await QRCode.toDataURL(url, {
        errorCorrectionLevel: 'M',
        margin: 1,
        scale: 8,
        color: {
          dark: '#0f172a',
          light: '#ffffffff',
        },
      });

      if (this.welcomeQrCodeSourceUrl !== url) {
        return;
      }

      this.welcomeQrCodeDataUrl = qrCodeDataUrl;
      this.welcomeQrCodeError = '';
      this.render();
    } catch {
      if (this.welcomeQrCodeSourceUrl !== url) {
        return;
      }

      this.welcomeQrCodeDataUrl = '';
      this.welcomeQrCodeError = translate('tenantWelcome.qrError');
      this.render();
    }
  }

  private async printWelcomeQrCode(): Promise<void> {
    const publicUrl = this.welcomeDraft?.publicUrl;
    if (!publicUrl) {
      return;
    }

    const qrCodeDataUrl = this.welcomeQrCodeDataUrl || await QRCode.toDataURL(publicUrl, {
      errorCorrectionLevel: 'M',
      margin: 1,
      scale: 8,
      color: {
        dark: '#0f172a',
        light: '#ffffffff',
      },
    });

    this.welcomeQrCodeDataUrl = qrCodeDataUrl;
    const popup = window.open('', '_blank', 'width=540,height=720');
    if (!popup) {
      this.copyFeedback = translate('tenantWelcome.printQrError');
      this.render();
      return;
    }

    try {
      popup.opener = null;
    } catch {
      // Ignore browsers that expose opener as read-only.
    }

    const tenantName = this.tenant ? `${this.tenant.firstName} ${this.tenant.lastName}`.trim() : '';
    const title = this.welcomeDraft?.pageTitle || translate('tenantWelcome.sectionTitle');
    popup.document.open();
    popup.document.write(`<!doctype html>
<html lang="${i18nService.currentLocale}">
  <head>
    <meta charset="utf-8">
    <title>${escapeHtml(translate('tenantWelcome.printTitle'))}</title>
    <style>
      body { font-family: Georgia, serif; margin: 0; padding: 24px; color: #0f172a; }
      .sheet { border: 2px solid #0f172a; border-radius: 20px; padding: 24px; max-width: 420px; margin: 0 auto; text-align: center; }
      .eyebrow { font-size: 12px; text-transform: uppercase; letter-spacing: .18em; color: #475569; margin-bottom: 12px; }
      h1 { font-size: 28px; margin: 0 0 8px; line-height: 1.1; }
      p { margin: 0 0 12px; line-height: 1.5; }
      img { width: 240px; height: 240px; display: block; margin: 20px auto; }
      .url { font-size: 12px; color: #475569; word-break: break-word; }
      @media print { body { padding: 0; } .sheet { border: none; max-width: none; } }
    </style>
  </head>
  <body>
    <section class="sheet">
      <div class="eyebrow">${escapeHtml(translate('tenantWelcome.qrPrintEyebrow'))}</div>
      <h1>${escapeHtml(title)}</h1>
      ${tenantName ? `<p>${escapeHtml(tenantName)}</p>` : ''}
      <p>${escapeHtml(translate('tenantWelcome.qrPrintBody'))}</p>
      <img src="${qrCodeDataUrl}" alt="${escapeHtml(translate('tenantWelcome.qrAlt'))}">
      <div class="url">${escapeHtml(publicUrl)}</div>
    </section>
  </body>
</html>`);
    popup.document.close();
    popup.onload = () => {
      popup.focus();
      popup.print();
    };
  }

  private addWelcomeContact(): void {
    const draft = this.getCurrentWelcomeDraft();
    draft.emergencyContacts = [...draft.emergencyContacts, { label: '', name: '', phone: '' }];
    this.welcomeDraft = draft;
    this.render();
  }

  private removeWelcomeContact(index: number): void {
    const draft = this.getCurrentWelcomeDraft();
    draft.emergencyContacts = draft.emergencyContacts.filter((_, currentIndex) => currentIndex !== index);
    this.welcomeDraft = draft;
    this.render();
  }

  private async saveWelcomePage(isActive: boolean): Promise<void> {
    if (!this.tenant || this.welcomeSaving) return;

    const draft = this.getCurrentWelcomeDraft();
    this.welcomeSaving = true;
    this.welcomeSaveError = '';
    this.copyFeedback = '';
    this.render();

    try {
      const saved = await tenantWelcomePagesApi.save(this.tenant.id, {
        isActive,
        pageTitle: draft.pageTitle,
        introduction: draft.introduction,
        houseRules: draft.houseRules,
        wifiName: draft.wifiName,
        wifiPassword: draft.wifiPassword,
        trashCollectionInfo: draft.trashCollectionInfo,
        parkingInfo: draft.parkingInfo,
        additionalInfo: draft.additionalInfo,
        emergencyContacts: draft.emergencyContacts,
      });

      this.welcomePage = saved;
      this.welcomeDraft = this.cloneWelcomePage(saved);
      this.welcomeQrCodeDataUrl = '';
      this.welcomeQrCodeSourceUrl = '';
      this.welcomeQrCodeError = '';
      this.tenant.welcomePage = {
        isConfigured: saved.isConfigured,
        isActive: saved.isActive,
        isTenantCurrent: saved.isTenantCurrent,
      };
      this.dispatchSliceEvent('tenant-welcome-page-updated', {
        tenantId: this.tenant.id,
        welcomePage: this.tenant.welcomePage,
      });
    } catch (err: unknown) {
      this.welcomeSaveError = err instanceof Error ? err.message : translate('common.errorGeneric');
    } finally {
      this.welcomeSaving = false;
    }

    this.render();
  }

  private cloneWelcomePage(page: TenantWelcomePageDto): TenantWelcomePageDto {
    return {
      ...page,
      emergencyContacts: page.emergencyContacts.map((contact) => ({ ...contact })),
    };
  }

  private getCurrentWelcomeDraft(): TenantWelcomePageDto {
    return this.welcomeDraft
      ? this.cloneWelcomePage(this.welcomeDraft)
      : this.getEmptyWelcomeDraft();
  }

  private getEmptyWelcomeDraft(): TenantWelcomePageDto {
    return {
      tenantId: this.tenant?.id ?? '',
      isConfigured: false,
      isActive: false,
      isTenantCurrent: this.tenant?.welcomePage?.isTenantCurrent ?? false,
      publicUrl: null,
      pageTitle: '',
      introduction: '',
      houseRules: '',
      wifiName: '',
      wifiPassword: '',
      trashCollectionInfo: '',
      parkingInfo: '',
      additionalInfo: '',
      emergencyContacts: [],
      updatedAt: null,
    };
  }

  private dispatchSliceEvent(name: string, detail: Record<string, unknown>): void {
    this.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      detail,
    }));
  }
}

if (!customElements.get('rc-tenant-accordion')) {
  customElements.define('rc-tenant-accordion', RcTenantAccordion);
}
